import { cn } from "@/lib/utils";

import type { ReportProgress } from "./use-report-progress";

interface ReportProgressBarProps {
  progress: ReportProgress;
  className?: string;
}

/**
 * Phase label + progress bar shown inside in-progress report rows. See
 * [[report-progress-bar.spec]] for the behavior contract. The bar's width
 * is animated via a CSS width transition so the simulation feels smooth
 * between 500ms ticks.
 */
export function ReportProgressBar({
  progress,
  className,
}: ReportProgressBarProps) {
  return (
    <div
      data-slot="report-progress-bar"
      className={cn("flex w-full flex-col gap-1", className)}
    >
      <p
        // Re-keying on the phase label nudges Tailwind's animate-fade-in
        // keyframe to replay each time the phase changes; motion-reduce
        // disables the fade per the spec's accessibility note.
        key={progress.phaseLabel}
        aria-live="polite"
        className="truncate text-xs text-muted-foreground motion-reduce:animate-none"
      >
        {progress.phaseLabel}
      </p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] ease-out motion-reduce:transition-none"
          style={{
            width: `${progress.progress}%`,
            transitionDuration: progress.isCompleting ? "600ms" : "700ms",
          }}
        />
      </div>
    </div>
  );
}
